/* eslint-disable @typescript-eslint/no-explicit-any */
/* ────────────────────────────────────────────────────────────────────────
   ViewSalaryStructure — clean read-only details SCREEN (routed).

   Receives the record via router state, normalises it with recordToForm, and
   renders only the sections relevant to its structure type's template. An
   [Edit Structure] button routes to the edit screen carrying the same record.
   ──────────────────────────────────────────────────────────────────────── */
import React from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import Button from "components/Button";
import { inferStructureType, recordToForm } from "./salaryStructure.mapper";
import { TYPE_BADGE, StructureType, templateForType } from "./salaryStructure.config";
import { monthsLabel, formatINR } from "./salaryStructure.calc";

const LIST_PATH = "/admin/salaryStructure";

/**
 * Detail card. `columns` controls the internal definition-list density so short
 * fields (e.g. Basic Information) can use 3 columns while richer sections use 2.
 * `break-inside-avoid` keeps a card whole inside the masonry column layout.
 */
const Card = ({
  title,
  children,
  columns = 2,
}: {
  title: string;
  children: React.ReactNode;
  columns?: 2 | 3;
}) => (
  <section className="break-inside-avoid rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
    <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 dark:border-gray-700/70">
      <span className="h-3.5 w-1 rounded-full bg-blue-600 dark:bg-blue-500" aria-hidden />
      <h3 className="text-[12px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
        {title}
      </h3>
    </div>
    <dl
      className={`grid grid-cols-1 gap-x-8 gap-y-4 p-5 ${columns === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"
        }`}
    >
      {children}
    </dl>
  </section>
);

const Item = ({
  label,
  value,
  full = false,
}: {
  label: string;
  value: React.ReactNode;
  full?: boolean;
}) => (
  <div className={`min-w-0 ${full ? "sm:col-span-2 lg:col-span-3" : ""}`}>
    <dt className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</dt>
    <dd className="text-sm font-medium text-slate-800 dark:text-slate-100 mt-1 break-words">
      {value ?? "—"}
    </dd>
  </div>
);

const applicable = (on: any) => (on ? "Applicable" : "Not Applicable");
const dualValue = (amount: any, isPct: any, suffix = "/ year") =>
  amount == null || amount === "" ? "—" : isPct ? `${amount}% of Basic` : `${formatINR(Number(amount))} ${suffix}`;

const ViewSalaryStructure = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const record = (location.state as { record?: any } | null)?.record ?? null;

  if (!record) {
    return <Navigate to={LIST_PATH} replace />;
  }

  const type: StructureType = inferStructureType(record);
  const template = templateForType(type);
  const v = recordToForm(record);
  const badge = TYPE_BADGE[type];

  // Type-specific cards are collected into an array so the layout can decide
  // between a packed masonry (multiple cards) and a single full-width card
  // (CONTRACT has only one) — this is what removes the large right-side gap.
  const detailCards: React.ReactNode[] = [];

  if (template === "EMPLOYEE") {
    detailCards.push(
      <Card key="salary" title="Salary Components">
        <Item label="Basic Salary" value={`${v.basicSalaryPercentage ?? "—"}% of CTC`} />
        <Item label="House Rent Allowance" value={`${v.hraPercentage ?? "—"}% of Basic`} />
        <Item label="Half-Year Bonus" value={`${v.halfYearBonusPercentage ?? 0}% of Basic`} />
        <Item label="Performance Bonus" value={`${v.performanceBonusPercentage ?? 0}% of Basic`} />
        <Item
          label="Annual Bonus"
          value={
            v.annualBonusPercentage == null
              ? "—"
              : v.annualBonusIsPercentage !== false
                ? `${v.annualBonusPercentage}% of Basic`
                : formatINR(Number(v.annualBonusPercentage))
          }
        />
      </Card>,
      <Card key="allowances" title="Allowances">
        <Item label="Conveyance" value={dualValue(v.conveyanceAllowance, v.conveyanceIsPercentage)} />
        <Item label="Medical Allowance" value={dualValue(v.medicalAllowance, v.medicalAllowanceIsPercentage)} />
        <Item label="Leave Encashment — Eligible Days" value={v.leaDays ?? "—"} />
        <Item label="Leave Encashment" value={dualValue(v.leaMonthly, v.leaIsPercentage, "/ month")} />
      </Card>,
      <Card key="benefits" title="Benefits & Deductions">
        <Item label="Medical Insurance" value={applicable(v.medicalInsuranceApplicable)} />
        <Item
          label="Provident Fund"
          value={
            v.pfApplicable
              ? `Applicable · ${v.providentFundEmployeePercentage}% / ${v.providentFundEmployerPercentage}%`
              : "Not Applicable"
          }
        />
        <Item label="Employee State Insurance" value={applicable(v.esiApplicable)} />
        <Item
          label="Professional Tax"
          value={v.profTaxApplicable ? `Applicable · ${formatINR(Number(v.taxOnProfession || 0))}` : "Not Applicable"}
        />
        <Item label="Noida Professional Tax" value={applicable(v.noidaPtApplicable)} />
      </Card>
    );
  }

  if (template === "CONTRACT") {
    detailCards.push(
      <Card key="contract" title="Contract Details">
        <Item label="Contract Type" value={v.contractType === "GST" ? "With GST" : "Without GST"} />
        <Item label="Contract Value" value={v.contractValue != null ? `${formatINR(Number(v.contractValue))} / year` : "—"} />
        <Item label="Monthly Payout" value={v.monthlyPayout != null ? `${formatINR(Number(v.monthlyPayout))} / month` : "—"} />
        <Item label="TDS Rate" value={v.tdsRate != null ? `${v.tdsRate}%` : "—"} />
        {v.contractType === "GST" && <Item label="GST %" value={v.gstPercentage != null ? `${v.gstPercentage}%` : "—"} />}
      </Card>
    );
  }

  if (template === "TRAINEE") {
    detailCards.push(
      <Card key="comp" title="Compensation">
        <Item label="Monthly Stipend" value={v.monthlyStipend != null ? `${formatINR(Number(v.monthlyStipend))} / month` : "—"} />
        <Item label="Training Period" value={monthsLabel(v.periodMonths)} />
        <Item
          label="Total Contract Value"
          value={
            v.monthlyStipend != null && v.periodMonths != null
              ? formatINR(Number(v.monthlyStipend) * Number(v.periodMonths))
              : "—"
          }
        />
      </Card>,
      <Card key="trainee-benefits" title="Benefits">
        <Item label="Provident Fund" value={applicable(v.pfApplicable)} />
        <Item label="Employee State Insurance" value={applicable(v.esiApplicable)} />
        <Item label="Medical Insurance" value={applicable(v.insuranceApplicable)} />
      </Card>
    );
  }

  // One card → keep it full width; multiple → pack them into a 2-column masonry
  // so cards of unequal height tile without leaving vertical gaps.
  const detailLayoutClass =
    detailCards.length > 1
      ? "gap-5 lg:columns-2 [&>section]:mb-5 [&>section:last-child]:mb-0"
      : "space-y-5";

  return (
    <div className="text-slate-900 dark:text-white bg-slate-100 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      <div className="max-w-6xl mx-auto w-full">
        {/* Screen header — aligned to the same column as the content below */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-6 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(LIST_PATH)}
              className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700"
              title="Back to Salary Structures"
            >
              <FaArrowLeft className="w-3.5 h-3.5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg font-bold truncate">{v.structureName || "Salary Structure"}</h1>
              {/* <div className="flex items-center gap-2 mt-0.5">
                <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${badge.className}`}>{badge.label}</span>
              </div> */}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${badge.className}`}>{badge.label}</span>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => navigate("/admin/salaryStructure/edit", { state: { record } })}
            className="shrink-0 !text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300"
          >
            Edit Structure
          </Button>
        </div>

        <div className="px-4 md:px-6 pb-12 space-y-5">
          {/* Basic Information — full-width strip; short fields laid out across
              three columns, description spans the full row for readability. */}
          <Card title="Basic Information" columns={3}>
            <Item label="Structure Name" value={v.structureName} />
            <Item label="Structure Type" value={badge.label} />
            <Item label="Description" value={v.description || "—"} full />
          </Card>

          <div className={detailLayoutClass}>{detailCards}</div>
        </div>
      </div>
    </div>
  );
};

export default ViewSalaryStructure;
